import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Send } from "lucide-react";
import { photo, photoSrcSet } from "../assets/photos";
import Seo from '../components/Seo';
import HeroVideo from '../components/HeroVideo';

/* ── Scroll-triggered reveal ─────────────────────────────────────── */
const Reveal = ({
  children,
  delay = 0,
  className = "",
  y = 28,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  y?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ── FAQs (rendered below and exposed as FAQPage structured data) ── */
const FAQS = [
              {
                q: 'How far in advance should we book?',
                a: 'We recommend at least 6 months in advance — but as long as we\'re available on your date, you can still secure your booking with us.',
              },
              {
                q: 'How do we know you\'ll get all the important moments?',
                a: 'In your Final Meeting we create a tailored timeline and family photo list together, so nothing is missed on the day.',
              },
              {
                q: 'What is your cancellation policy?',
                a: 'We\'ll do our best to move your booking to a new date in the same year at no extra cost. If we can\'t accommodate the new date, our standard cancellation policy applies.',
              },
              {
                q: 'Can we see previews quickly after the wedding?',
                a: 'Yes — you\'ll receive your Preview Photos within 24 hours and your Preview Video Reel within 48 hours.',
              },
              {
                q: 'How long until we receive everything?',
                a: 'Full delivery of your complete package is within 4 months of your wedding day.',
              },
              {
                q: 'Can we trust you to guide us through the day?',
                a: 'Absolutely. We act as silent partners — keeping the schedule on track without ever being intrusive.',
              },
              {
                q: 'How would you describe your style?',
                a: 'Candid and documentary at heart, but we guide and direct you throughout to keep every moment feeling natural and true.',
              },
              {
                q: 'Do you provide raw files?',
                a: 'We don\'t provide raw files. The delivered product is the full story of your day — curated and edited with care.',
              },
              {
                q: 'Can we see full galleries before booking?',
                a: 'Yes. A few full wedding galleries and videos are available on our website, and our social media pages show the breadth of our work.',
              },
            ];

/* ── Home ─────────────────────────────────────────────────────────── */
const Home = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    weddingDate: "",
    venue: "",
    message: "",
    services: { photography: false, videography: false, combo: false },
    referral: {
      instagram: false,
      facebook: false,
      otherSupplier: false,
      friendsFamily: false,
      googleAds: false,
    },
  });

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleServiceCheck = (field: keyof typeof formData.services) => {
    setFormData((prev) => ({
      ...prev,
      services: { ...prev.services, [field]: !prev.services[field] },
    }));
  };

  const handleReferralCheck = (field: keyof typeof formData.referral) => {
    setFormData((prev) => ({
      ...prev,
      referral: { ...prev.referral, [field]: !prev.referral[field] },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Inquiry submitted:", formData);
  };

  const journey = [
    {
      n: "01",
      title: "Intro Meeting",
      desc: "We meet to discuss your wedding vision and get to know each other. Once you're happy, we secure your date with a signed contract and booking fee.",
    },
    {
      n: "02",
      title: "Welcome Email",
      desc: "You'll receive everything you need — planning information, engagement session details, suggested locations, and insider tips for the journey ahead.",
    },
    {
      n: "03",
      title: "Engagement Session",
      desc: "A complimentary shoot to get comfortable in front of the camera — and a chance for us to understand your dynamic before the big day.",
    },
    {
      n: "04",
      title: "Online Planning Platform",
      desc: "Our platform keeps everything in one place — your next steps, FAQ, and scheduling for Q&A calls throughout the planning process.",
    },
    {
      n: "05",
      title: "Final Meeting",
      desc: "About one month before your wedding, we finalise your timeline and compile your formal family photo list so nothing is missed.",
    },
    {
      n: "06",
      title: "Your Day & Delivery",
      desc: "Preview gallery within 24 hours. Preview video reel within 48 hours. Full gallery and USB within 4 months of your wedding day.",
    },
  ];

  return (
    <div className="overflow-x-hidden bg-ivory">
      <Seo route="/" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      {/* ════════════════════════════════════════════════════════════
          HERO — Cinematic fullscreen
      ════════════════════════════════════════════════════════════ */}
      <section className="relative h-[70vh] md:h-screen min-h-[500px] overflow-hidden film-grain">
        {/* Video background */}
        <HeroVideo />

        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-ink/35 via-ink/10 to-ink/80" />

        {/* Top metadata strip */}
        <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center px-8 md:px-14 lg:px-20 pt-24 md:pt-28 [@media(max-height:799px)]:hidden">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.6 }}
            className="label-sm text-white/80"
          >
            Est. 2019
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.8 }}
            className="label-sm text-white/80 hidden sm:block"
          >
            Pretoria, South Africa
          </motion.span>
        </div>

        {/* Hero copy — bottom left */}
        <div className="absolute inset-0 z-10 flex items-end px-8 md:px-14 lg:px-20 pb-20 md:pb-28">
          <div className="max-w-[700px]">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.4 }}
              className="label-sm text-white/80 mb-8"
            >
              George &amp; Co.
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 44 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.5,
                delay: 0.6,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="heading-display text-white text-[clamp(2.75rem,min(7.5vw,13vh),7.5rem)]"
            >
              We preserve
              <br />
              <span className="heading-italic">what time</span>
              <br />
              cannot.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.2,
                delay: 1.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="body-light text-white/85 mt-7 max-w-[340px]"
            >
              Wedding Photography &amp; Videography.
              <br />
              Every frame, a memory made permanent.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.55 }}
              className="mt-10"
            >
              <Link to="/contact" className="link-underline text-white/90">
                Begin your story
                <span className="text-base font-light ml-1">→</span>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator — right edge */}
        <div className="absolute right-8 md:right-14 bottom-20 z-10 flex flex-col items-center gap-3">
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 1.6, delay: 2, ease: [0.16, 1, 0.3, 1] }}
            className="w-px h-14 bg-white/25 origin-top"
          />
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2.4 }}
            className="label-sm text-white/80"
            style={{ writingMode: "vertical-rl" }}
          >
            Scroll
          </motion.span>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          PHILOSOPHY — "These are not photographs."
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-ink py-24 md:py-40">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0 items-center">
            {/* Image */}
            <Reveal delay={0} className="lg:col-span-7 overflow-hidden">
              <div className="overflow-hidden">
                <img loading="lazy" decoding="async"
                  src={photo("sarah-simon/couple-5.jpg")}
                  alt="Wedding couple Sarah & Simon — photographed by George & Co."
                  className="w-full h-[55vw] max-h-[72vh] min-h-[320px] object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                />
              </div>
            </Reveal>

            {/* Copy */}
            <div className="lg:col-span-5 lg:pl-16 xl:pl-20 flex flex-col justify-center">
              <Reveal delay={0.2}>
                <p className="label-sm text-champagne mb-8">Our Philosophy</p>
              </Reveal>
              <Reveal delay={0.35}>
                <h2 className="heading-italic text-white text-[clamp(2.2rem,4.5vw,4.5rem)] leading-[1.06] mb-8">
                  "These are not
                  <br />
                  photographs."
                </h2>
              </Reveal>
              <Reveal delay={0.5} className="space-y-5 mb-12">
                <p className="body-light text-white/55">
                  They are the glance exchanged when no one else was watching.
                  The trembling of hands before the vows. The way light fell
                  across your face at exactly the right moment.
                </p>
                <p className="body-light text-white/55">
                  We are memory architects. We preserve the unrepeatable — with
                  intention, artistry, and absolute care.
                </p>
              </Reveal>
              <Reveal delay={0.6}>
                <Link to="/gallery" className="link-underline text-white/60">
                  View our archive
                  <span className="text-base font-light ml-1">→</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SELECTED WORK — Editorial asymmetric grid
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-ivory py-24 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal className="flex justify-between items-end mb-12 md:mb-14">
            <div>
              <p className="label-sm text-mist mb-4">Selected Work</p>
              <h2 className="heading-display text-ink text-[clamp(2.4rem,5vw,5rem)]">
                The Archive
              </h2>
            </div>
            <Link
              to="/gallery"
              className="link-underline text-ink hidden md:inline-flex"
            >
              Full gallery <span className="ml-1">→</span>
            </Link>
          </Reveal>

          {/* Row 1: 8/4 split */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
            <Reveal delay={0} className="md:col-span-8 overflow-hidden">
              <div className="overflow-hidden h-[50vw] max-h-[580px] min-h-[280px]">
                <img loading="lazy" decoding="async"
                  src={photo("janelle-gareth/5.jpg")}
                  alt="Janelle & Gareth"
                  className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                />
              </div>
            </Reveal>
            <div className="md:col-span-4 grid grid-rows-2 gap-3">
              <Reveal delay={0.12} className="overflow-hidden">
                <div className="overflow-hidden h-[24vw] max-h-[287px] min-h-[160px]">
                  <img loading="lazy" decoding="async"
                    src={photo("mosa-kudzai/5.jpg")}
                    srcSet={photoSrcSet("mosa-kudzai/5.jpg")}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    alt="Mosa & Kudzai"
                    className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.22} className="overflow-hidden">
                <div className="overflow-hidden h-[24vw] max-h-[287px] min-h-[160px]">
                  <img loading="lazy" decoding="async"
                    src={photo("kelsey-armand/10.jpg")}
                    srcSet={photoSrcSet("kelsey-armand/10.jpg")}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    alt="Kelsey & Armand"
                    className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                  />
                </div>
              </Reveal>
            </div>
          </div>

          {/* Row 2: equal thirds */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { img: "amber-erik/10.jpg", alt: "Amber & Erik" },
              { img: "mel-brandon/10.jpg", alt: "Mel & Brandon" },
              { img: "dawn-henko/10.jpg", alt: "Dawn & Henko" },
            ].map(({ img, alt }, i) => (
              <Reveal key={i} delay={i * 0.1} className="overflow-hidden">
                <div className="overflow-hidden h-[30vw] max-h-[340px] min-h-[180px]">
                  <img loading="lazy" decoding="async"
                    src={photo(img)}
                    srcSet={photoSrcSet(img)}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    alt={alt}
                    className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                  />
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-8 flex md:hidden">
            <Link to="/gallery" className="link-underline text-ink">
              Full gallery <span className="ml-1">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          OUR CRAFT — Photography & Videography
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-bone py-24 md:py-40">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">Our Craft</p>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="heading-display text-ink text-[clamp(2.4rem,5.5vw,5.5rem)] mb-16 md:mb-24 leading-[1.0]">
              From First Kiss
              <br />
              <span className="heading-italic">to Last Dance</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {/* Photography */}
            <div>
              <Reveal className="overflow-hidden mb-8">
                <div className="overflow-hidden h-[45vw] max-h-[520px] min-h-[260px]">
                  <img loading="lazy" decoding="async"
                    src={photo("michelle-et/bridal-5.jpg")}
                    alt="Wedding photography by George & Co."
                    className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="label-sm text-mist mb-3">Photography</p>
                <h3 className="heading-display text-ink text-3xl md:text-4xl mb-5">
                  Everlasting frames
                </h3>
                <p className="body-light mb-8">
                  We combine candid documentary storytelling with carefully
                  directed moments — capturing your wedding authentically. You
                  don't need to know your angles; we'll guide you so you can
                  simply be present.
                </p>
                <Link to="/services" className="link-underline text-ink">
                  Explore <span className="ml-1">→</span>
                </Link>
              </Reveal>
            </div>

            {/* Videography — offset on desktop */}
            <div className="md:mt-20">
              <Reveal className="overflow-hidden mb-8">
                <div className="overflow-hidden h-[45vw] max-h-[520px] min-h-[260px]">
                  <img loading="lazy" decoding="async"
                    src={photo("cezanne-dewyk/5.jpg")}
                    alt="Wedding table styling — wedding videography by George & Co."
                    className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="label-sm text-mist mb-3">Videography</p>
                <h3 className="heading-display text-ink text-3xl md:text-4xl mb-5">
                  Your story in motion
                </h3>
                <p className="body-light mb-8">
                  Your love story preserved not just as it looked, but as it
                  felt — your vows, the laughter, tears of joy, the music that
                  filled the room. Film returns the full sensory experience of
                  your day, again and again.
                </p>
                <Link to="/services" className="link-underline text-ink">
                  Explore <span className="ml-1">→</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          NUMBERS — Editorial stat strip
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-charcoal py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-8">
            {(
              [
                { n: "500+", label: "Couples who\ntrusted their story to us" },
                { n: "11+", label: "Years of dedicated\ncraftsmanship" },
                { n: "35+", label: "Venues across\nSouth Africa & beyond" },
                { n: "7+", label: "Countries we've\ncaptured love in" },
              ] as const
            ).map(({ n, label }, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="heading-display text-white text-[clamp(2.8rem,5.5vw,5rem)] mb-3">
                  {n}
                </div>
                <p
                  className="label-sm text-white/60"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {label}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          JOURNEY — From inquiry to forever
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-ivory py-24 md:py-40">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">The Experience</p>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="heading-display text-ink text-[clamp(2.4rem,5vw,5rem)] mb-16 md:mb-24 leading-[1.0]">
              From first inquiry
              <br />
              <span className="heading-italic">to forever</span>
            </h2>
          </Reveal>

          <div className="divide-y divide-silver/40">
            {journey.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.06} y={16}>
                <div className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 group">
                  <div className="col-span-2 md:col-span-1 pt-1">
                    <span className="label-sm text-mist">{step.n}</span>
                  </div>
                  <div className="col-span-10 md:col-span-4">
                    <h3 className="heading-display text-ink text-xl md:text-2xl group-hover:text-champagne transition-colors duration-500">
                      {step.title}
                    </h3>
                  </div>
                  <div className="col-span-12 md:col-span-7 md:pl-4">
                    <p className="body-light text-sm md:text-[0.9375rem]">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TESTIMONIAL — Dark cinematic quote
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-charcoal py-24 md:py-40">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-champagne mb-12 md:mb-16">
              What they say
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <blockquote className="heading-italic text-white text-[clamp(1.6rem,4vw,3.75rem)] leading-[1.2] max-w-4xl mb-12 md:mb-16">
              "You guided us through everything and made us feel so comfortable
              and relaxed. You have an absolutely incredible team and we are so
              grateful."
            </blockquote>
          </Reveal>
          <Reveal
            delay={0.3}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6"
          >
            <div>
              <p className="label-sm text-white/55 mb-1">Keara &amp; Alessio</p>
              <p className="label-sm text-white/60">
                Rosemary Hill · June 2021
              </p>
            </div>
            <Link to="/testimonials" className="link-underline text-white/60">
              More stories <span className="ml-1">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          FAQ — Questions couples don't think to ask
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-bone py-24 md:py-40">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">Before You Ask</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="heading-display text-ink text-[clamp(2rem,4.5vw,4.5rem)] mb-16 md:mb-20">
              Questions couples don't
              <br />
              <span className="heading-italic">think to ask</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 divide-y md:divide-y-0">
            {FAQS.map((faq, i) => (
              <Reveal key={i} delay={i * 0.04} y={16}>
                <div className={`py-8 border-t border-silver/40 ${i < 2 ? 'md:border-t-0' : ''}`}>
                  <h3 className="heading-display text-ink text-lg md:text-xl mb-3">{faq.q}</h3>
                  <p className="body-light text-sm md:text-[0.9375rem]">{faq.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTACT INQUIRY — Begin your story
      ════════════════════════════════════════════════════════════ */}
      <section className="bg-ivory py-24 md:py-40">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
            {/* Brand copy — left */}
            <div className="lg:col-span-4">
              <Reveal>
                <p className="label-sm text-mist mb-6">Begin Your Story</p>
              </Reveal>
              <Reveal delay={0.12}>
                <h2 className="heading-display text-ink text-[clamp(2.2rem,4vw,4rem)] mb-8 leading-[1.05]">
                  Let us preserve
                  <br />
                  <span className="heading-italic">your moments.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.22}>
                <p className="body-light mb-12">
                  We take on a limited number of weddings each year to ensure
                  every couple receives the focus and care their day deserves.
                  Reach out and let's begin.
                </p>
              </Reveal>
              <Reveal delay={0.3} className="space-y-4">
                <a
                  href="mailto:info@georgeandco.co.za"
                  className="link-underline text-ink block"
                >
                  info@georgeandco.co.za
                </a>
                <p className="label-sm text-mist">
                  Pretoria · Available Worldwide
                </p>
              </Reveal>
            </div>

            {/* Form — right */}
            <Reveal delay={0.15} className="lg:col-span-8">
              <form onSubmit={handleSubmit} className="space-y-9">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                  <div>
                    <label className="label-sm text-mist block mb-2">
                      Couple Names *
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInput}
                      placeholder="Your names"
                      className="input-cinematic"
                    />
                  </div>
                  <div>
                    <label className="label-sm text-mist block mb-2">
                      Email *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInput}
                      placeholder="your@email.com"
                      className="input-cinematic"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                  <div>
                    <label className="label-sm text-mist block mb-2">
                      Wedding Date
                    </label>
                    <input
                      name="weddingDate"
                      type="date"
                      value={formData.weddingDate}
                      onChange={handleInput}
                      className="input-cinematic"
                    />
                  </div>
                  <div>
                    <label className="label-sm text-mist block mb-2">
                      Venue
                    </label>
                    <input
                      name="venue"
                      type="text"
                      value={formData.venue}
                      onChange={handleInput}
                      placeholder="Your wedding venue"
                      className="input-cinematic"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                  {/* Services */}
                  <div>
                    <label className="label-sm text-mist block mb-5">
                      Interested in *
                    </label>
                    <div className="space-y-3.5">
                      {(
                        [
                          ["photography", "Photography"],
                          ["videography", "Videography"],
                          ["combo", "Photo & Video Combo"],
                        ] as [keyof typeof formData.services, string][]
                      ).map(([key, lbl]) => (
                        <label
                          key={key}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <div
                            className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                              formData.services[key]
                                ? "bg-ink border-ink"
                                : "border-silver group-hover:border-ink/40"
                            }`}
                          >
                            {formData.services[key] && (
                              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5">
                                <polyline
                                  points="1.5,5 4,7.5 8.5,2"
                                  stroke="white"
                                  strokeWidth="1.4"
                                  fill="none"
                                />
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.services[key]}
                            onChange={() => handleServiceCheck(key)}
                            className="sr-only"
                          />
                          <span className="label-sm text-mist group-hover:text-ink transition-colors duration-300">
                            {lbl}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Referral */}
                  <div>
                    <label className="label-sm text-mist block mb-5">
                      How did you find us?
                    </label>
                    <div className="space-y-3.5">
                      {(
                        [
                          ["instagram", "Instagram"],
                          ["facebook", "Facebook"],
                          ["friendsFamily", "Friends & Family"],
                          ["otherSupplier", "Another Supplier"],
                          ["googleAds", "Google"],
                        ] as [keyof typeof formData.referral, string][]
                      ).map(([key, lbl]) => (
                        <label
                          key={key}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <div
                            className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                              formData.referral[key]
                                ? "bg-ink border-ink"
                                : "border-silver group-hover:border-ink/40"
                            }`}
                          >
                            {formData.referral[key] && (
                              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5">
                                <polyline
                                  points="1.5,5 4,7.5 8.5,2"
                                  stroke="white"
                                  strokeWidth="1.4"
                                  fill="none"
                                />
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.referral[key]}
                            onChange={() => handleReferralCheck(key)}
                            className="sr-only"
                          />
                          <span className="label-sm text-mist group-hover:text-ink transition-colors duration-300">
                            {lbl}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="label-sm text-mist block mb-2">
                    Your Story
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInput}
                    rows={4}
                    placeholder="Tell us about your day..."
                    className="input-cinematic resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                  <button
                    type="submit"
                    className="flex items-center gap-4 bg-ink text-ivory px-10 py-4 label-sm hover:opacity-70 transition-opacity duration-500"
                  >
                    Send Inquiry
                    <Send size={13} />
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
